import React, { useState } from 'react';
import { useGenerateDbtJobsMutation, useCreateDatabaseMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectSnowflakeAccount, selectSelectedDatabase, setSelectedDatabase } from 'app/snowflakeSlice';
import { selectSource, selectDbtVersion, selectDbtExecutionMode } from '../reducers/dbtMigrationSlice';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import { classNames } from 'utils/styleUtils';
import StepWrapper from 'components/StepWrapper';
import InfoSection from 'components/InfoSection';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import DatabaseSelector from 'components/DatabaseSelector';

export interface TargetDatabaseStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function TargetDatabaseStep(props: TargetDatabaseStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const selectedDatabase = useAppSelector(selectSelectedDatabase);
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const source = useAppSelector(selectSource);
  const dbtVersion = useAppSelector(selectDbtVersion);
  const dbtExecutionMode = useAppSelector(selectDbtExecutionMode);

  const tabs = ['Create new database', 'Select existing database'];
  const [selectedTab, setSelectedTab] = useState<string>('Create new database');

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to generate dbt jobs');

  // State for new database creation
  const getDefaultDatabaseName = (): string => {
    // Extract project name from projectPath
    const projectNameMatch = projectPath.match(/([^/]+)$/);
    const projectName =
      projectNameMatch !== null ? projectNameMatch[1].toUpperCase().replaceAll(' ', '_').replaceAll('-', '_') : '';
    return `DATAOPS_${projectName}_PROD`;
  };

  const [newDatabaseName, setNewDatabaseName] = useState<string>(getDefaultDatabaseName());

  const [generateDbtJobs] = useGenerateDbtJobsMutation();
  const [createDatabase] = useCreateDatabaseMutation();

  const onContinue = (): void => {
    setLoading(true);
    setFailed(false);

    // If creating a new database
    if (selectedTab === 'Create new database' && newDatabaseName !== '') {
      createDatabase({
        database: newDatabaseName,
        account: snowflakeAccount,
      })
        .unwrap()
        .then(() => {
          // After creating the database, proceed with generating dbt jobs
          generateDbtJobs({
            projectName: projectPath,
            masterDatabase: newDatabaseName,
            account: snowflakeAccount,
            dbtVersion: source === 'sample' ? '1.7' : dbtVersion,
            branch,
            executionMode: dbtExecutionMode,
            jobList: ['BUILD'],
            seedSelector: '*',
            sourceSelector: '*',
            modelSelector: '*',
          })
            .unwrap()
            .then((res: any) => {
              console.log(res);
              props.onContinue();
            })
            .catch((err) => {
              console.log(err);
              setFailed(true);
              setErrorMessage(err.data);
              setLoading(false);
            });
        })
        .catch((err) => {
          console.log(err);
          setFailed(true);
          setErrorMessage(`Failed to create database: ${String(err.data)}`);
          setLoading(false);
        });
    } else {
      // Using existing database
      generateDbtJobs({
        projectName: projectPath,
        masterDatabase: selectedDatabase,
        account: snowflakeAccount,
        dbtVersion: source === 'sample' ? '1.7' : dbtVersion,
        branch,
        executionMode: dbtExecutionMode,
        jobList: ['BUILD'],
        seedSelector: '*',
        sourceSelector: '*',
        modelSelector: '*',
      })
        .unwrap()
        .then((res: any) => {
          console.log(res);
          props.onContinue();
        })
        .catch((err) => {
          console.log(err);
          setFailed(true);
          setErrorMessage(err.data);
          setLoading(false);
        });
    }
  };

  return (
    <StepWrapper
      title="Target database"
      subtitle="Select the target database"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      isLoading={loading}
      isFailed={failed}
      errorMessage={errorMessage}
      continueDisabled={
        (selectedTab === 'Select existing database' && selectedDatabase === '') ||
        (selectedTab === 'Create new database' && newDatabaseName === '')
      }
    >
      <div className="min-h-full mb-6 relative">
        <div className="w-3/4 wide:w-1/2 mx-[auto]">
          <div className="border-b border-gray-200 mb-6">
            <nav aria-label="Tabs" className="-mb-px flex justify-between rounded-md">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  onClick={() => {
                    setSelectedTab(tab);
                    if (tab === 'Create new database') {
                      const defaultName = getDefaultDatabaseName();
                      setNewDatabaseName(defaultName);
                      dispatch(setSelectedDatabase(defaultName));
                    } else {
                      dispatch(setSelectedDatabase(''));
                    }
                  }}
                  className={classNames(
                    selectedTab === tab
                      ? 'border-dataops-primary text-dataops-primary bg-dataops-primary/5'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                    'flex-1 whitespace-nowrap border-b-2 px-1 py-2 text-center text-sm font-medium cursor-pointer',
                  )}
                >
                  {tab}
                </div>
              ))}
            </nav>
          </div>

          <InfoSection
            text={
              selectedTab === 'Select existing database'
                ? 'Select one of your existing Snowflake databases as the target database for your project.'
                : 'Create a new Snowflake database for your project. The database will be created in your Snowflake account when you continue.'
            }
          />
        </div>
        {selectedTab === 'Select existing database' ? (
          <DatabaseSelector
            onSelect={(database) => {
              dispatch(setSelectedDatabase(database));
            }}
            selectedDatabases={selectedDatabase !== '' ? [selectedDatabase] : []}
          />
        ) : (
          <div className="w-3/4 wide:w-1/2 mx-[auto] mt-6">
            <label htmlFor="database-name" className="block text-sm font-medium leading-6 text-gray-900">
              Database name
            </label>
            <div className="mt-2">
              <div className="flex rounded-md shadow-sm">
                <input
                  type="text"
                  name="database-name"
                  id="database-name"
                  className="block rounded-md flex-1 ring-1 ring-inset ring-gray-300 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 sm:text-sm"
                  placeholder="Enter new database name"
                  value={newDatabaseName}
                  onChange={(e) => {
                    setNewDatabaseName(e.target.value);
                    dispatch(setSelectedDatabase(e.target.value));
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="absolute w-[calc(100%-4rem)] bottom-20">
        <div className="w-3/4 wide:w-1/2 mx-[auto]">
          <LoadingAndErrorSection
            isLoading={loading}
            isFailed={failed}
            errorMessage={errorMessage}
            hideLoading={true}
          />
        </div>
      </div>
    </StepWrapper>
  );
}
